import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, merge, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, take, takeUntil, timeout } from 'rxjs/operators';

import { EventsHubService } from '../../../core/events-hub.service';
import { AuthenticationService } from '../../../core/services';
import { ReleaseNoteService } from '../../../core/data-services/release-note.service';
import { isAuthRoute } from '../../../shared/constants/auth-routes';
import { SplashCard } from '../../../shared/models/release-note/release-note.model';
import { RELEASE_CARD_MODAL_OPTIONS, ReleaseCardComponent } from '../card/release-card.component';
import { afterModalsClose, OpenModals } from '../utils/release-note-modal.util';

const PROBE_TIMEOUT_MS = 5000;

/**
 * Shows the release splash once per user per release (RA-2695). Placed once in the app shell.
 *
 * Once a user is logged in — at page load with an existing session, or on the login event — it waits for
 * the first navigation to a non-authentication route and probes `GET /release-notes/splash` once per app
 * load (re-armed on logout). 🔴 It fails open: an error, a timeout, a 204 or a malformed body all mean do
 * nothing — no banner, no spinner, no permission check (`AuthorizationService.currentUser` is not set yet).
 * It never stacks on another dialog, and records the view however the card is closed.
 */
@Component({
  selector: 'rms-release-splash-host',
  template: '',
  standalone: false
})
export class ReleaseSplashHostComponent implements OnInit, OnDestroy {

  private probed = false;
  private armed: Subscription | null = null;
  private readonly openModals = new OpenModals();
  private readonly destroy$ = new Subject<void>();
  /** Fires on logout, cancelling a probe in flight or a card still waiting for other modals to close. */
  private readonly logout$ = new Subject<void>();

  constructor(
    private readonly eventsHubService: EventsHubService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
    private readonly releaseNoteService: ReleaseNoteService,
    private readonly modalService: NgbModal
  ) {}

  public ngOnInit(): void {
    const alreadyLoggedIn = this.authenticationService.isLoggedIn() && !!this.authenticationService.getLoggedUser();
    merge(of(alreadyLoggedIn), this.eventsHubService.isLoggedIn$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loggedIn => loggedIn ? this.arm() : this.reset());
  }

  public ngOnDestroy(): void {
    this.openModals.dismissAll();
    this.armed?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private arm(): void {
    if (this.probed || this.armed) {
      return;
    }
    if (this.router.navigated && !isAuthRoute(this.router.url)) {
      this.probe();
      return;
    }
    this.armed = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd && !isAuthRoute(event.urlAfterRedirects)),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.armed = null;
      this.probe();
    });
  }

  /** On logout: re-arm, and close a card left open so it cannot record a view with no session. */
  private reset(): void {
    this.logout$.next();
    this.openModals.dismissAll();
    this.armed?.unsubscribe();
    this.armed = null;
    this.probed = false;
  }

  private probe(): void {
    if (this.probed) {
      return;
    }
    this.probed = true;
    const until$ = merge(this.destroy$, this.logout$);
    this.releaseNoteService.getSplash().pipe(
      timeout(PROBE_TIMEOUT_MS),
      catchError(() => of(null)),
      takeUntil(until$)
    ).subscribe(card => {
      if (card?.type === 'RELEASE' && card.releaseId) {
        afterModalsClose(this.modalService, until$).subscribe(() => this.open(card));
      }
    });
  }

  private open(card: SplashCard): void {
    if (!this.authenticationService.isLoggedIn()) {
      return;
    }
    const ref = this.openModals.track(this.modalService.open(ReleaseCardComponent, { ...RELEASE_CARD_MODAL_OPTIONS }));
    Object.assign(ref.componentInstance, {
      context: 'splash',
      cover: card,
      releaseId: card.releaseId,
      mediaScope: 'public'
    });
    const seen = () => {
      // Dismissed by a logout: there is no session to record the view against.
      if (this.authenticationService.isLoggedIn()) {
        this.releaseNoteService.markSeen(card.releaseId).pipe(catchError(() => EMPTY)).subscribe();
      }
    };
    ref.result.then(seen, seen);
  }
}
